<template>
  <ProSpaceGeoLayout class="adjustments-grid" isParent>
    <template #top>
      <ProSpaceTopPanel :header="getPageTitle">
        <template #right>
          <ProSpaceButton
            style="padding-top: 4px; padding-bottom: 4px"
            icon="chevron-down"
            type="secondary"
            :label="currentPeriod?.name"
            @click="actionPeriod"
          />
          <ProSpaceIconButton
            icon="update"
            iconWidth="16px"
            iconHeight="16px"
            containerSize="20px"
            @click="actionUpdate"
          />
          <ProSpaceDots
            :options="dotsHeaderOptions"
            :isSquare="true"
            @action="(action) => handleHeaderDots(action)"
          />
        </template>
      </ProSpaceTopPanel>
    </template>
    <template #center>
      <ProSpaceGeoLayout>
        <template #top>
          <ProSpaceFilterPanel
            ref="filterPanel"
            filter-model="outletCode"
            :searchPlaceholder="$t('adjustmentSearchTxt')"
            :modelFastFilter="modelFastFilter"
            :fastFilters="fastFilters"
            :unicKeyStorage="adjustmentService.constructor.name + 'grid'"
            :noMountedRequest="true"
            :oneValueFilters="['status', 'disabled']"
            :txtResetFilters="$t('txtResetFilters')"
            @apply="submitFilter"
            @update:modelFastFilter="updateModelFastFilter($event)"
            onlyDigit
            equalsSearch
            :chips="{
              onlyNumbers: true,
              lengthValue: 18,
            }"
            :blocked-filters="needBlock"
            @block="selectedMultipleBlockFilterHandler"
          >
            <template #hint>
              {{ this.$t("filterBy") }}
            </template>
            <template #filters="{ filter }">
              <ProSpaceFilterSearchfield
                v-model="filter.hsrId"
                :service="hsrService"
                :txtTitleModal="$t('hsrList')"
                get-method="grid"
                option-label="value"
                option-value="id"
                scrollSelected
                :placeholder-value="$t('hsr')"
                :selectAllTxt="$t('selectAllTxt')"
                :closeTxt="$t('closeTxt')"
                :applyTxt="$t('applyTxt')"
                :cancelTxt="$t('cancelTxt')"
                :clearFilterTxt="$t('clearTxt')"
                @apply="(model) => customFilterApply(filter, model)"
                :default-filter="{
                  disabled: false,
                }"
                style="max-width: 200px"
              />
              <ProSpaceFilterSearchfield
                v-for="(searchField, index) in searchFieldsConfig"
                :key="searchField.model + index"
                v-model="filter[searchField.model]"
                :service="searchField.service"
                get-method="grid"
                optionLabel="value"
                :optionValue="searchField.optionValue"
                optionCode="code"
                scrollSelected
                :txtTitleModal="searchField.txtTitleModal"
                :placeholder-value="searchField.placeholderValue"
                :selectAllTxt="$t('selectAllTxt')"
                :cancelTxt="$t('cancelTxt')"
                :closeTxt="$t('closeTxt')"
                :applyTxt="$t('applyTxt')"
                :clearFilterTxt="$t('clearTxt')"
                @apply="(model) => customFilterApply(filter, model)"
                :default-filter="{
                  disabled: false,
                }"
                style="max-width: 200px"
              />

              <ProSpaceMultiSelect
                ref="filterResult"
                :localization="$t"
                v-model="filter.resultStatus"
                :service="adjustmentService"
                getMethod="getResultFilterTypes"
                optionLabel="text"
                optionValue="value"
                :selectAllTxt="$t('selectAllTxt')"
                :closeTxt="$t('closeTxt')"
                :placeholderValue="$t('Result')"
                :applyTxt="$t('applyTxt')"
                :clearFilterTxt="$t('clearTxt')"
                @apply="emitFilter(true)"
              />

              <ProSpaceCustomFilterPanel
                :customTxt="$t('custom')"
                :customFilterTxt="$t('customFilterTxt')"
                :selectAllTxt="$t('selectAllTxt')"
                :closeTxt="$t('closeTxt')"
                :applyTxt="$t('applyTxt')"
                :clearFilterTxt="$t('clearTxt')"
                :fromTxt="$t('fromTxt')"
                :toTxt="$t('toTxt')"
                :services="customServices"
                :unicKeyStorage="adjustmentService.constructor.name + 'grid'"
                @apply="(model) => customFilterApply(filter, model)"
              />
            </template>
          </ProSpaceFilterPanel>
        </template>
        <template #center>
          <ProSpaceMainGrid
            ref="grid"
            getMethod="grid"
            class="adjustments-grid"
            selectionMode="multiple"
            :service="adjustmentService"
            :noMountedRequest="true"
            :defaultSortOrder="-1"
            :defaultSortField="getDefaultSortingField"
            :loaderConfig="getLoaderConfig"
            :customHeightRow="117"
            :autoWidthStatusId="false"
            :defaultColumnWidths="[400, 600, 500, 100]"
            @selected-change="selectedHandler"
            @db-click="toItem"
            @loaded="gridLoadedHandler"
          >
            <template #columns>
              <Column
                field="id"
                :header="$t('addressProgramOutlet')"
                sortable
                class="adjustments-grid__column-store adjustments-grid__bordered-column"
                frozen
              >
                <template #body="field">
                  <div class="adjustments-grid__column-store-container">
                    <store-column
                      v-if="field.data && !getGridLoading"
                      class="column__single"
                      :data="field.data"
                      :status="getStatus(field.data.status)"
                      :isApproveDisplay="field.data.result"
                      :isStatusDisplay="false"
                      :isChangesDisplay="false"
                      codeLabel="id"
                      :wrapAddress="false"
                    />
                  </div>
                </template>
              </Column>
              <Column
                class="adjustments-grid__bordered-column adjustments-grid__sorting-table-header"
                :field="getSortingFieldValue"
                :header="`${$t('addressProgramParameters')} ${
                  currentSortingLabel ? '(' + currentSortingLabel + ')' : ''
                }`"
                sortable
              >
                <template #header="field">
                  <ProSpaceDropdown
                    v-model="selectCurrentSorting"
                    class="adjustments-grid__dropdown"
                    :options="sortingOptions"
                    :inputStyle="{ display: 'none' }"
                    propLabel="label"
                    propValue="value"
                    :dropdownIcon="[
                      'prospace-icon icon-sliders',
                      { 'prospace-icon--secondary': dropdownVisible },
                    ]"
                    filterIcon="icon-search prospace-icon"
                    :filterPlaceholder="$t('searchTxt')"
                    scrollHeight="245px"
                    filter
                    @hide="dropdownVisible = false"
                    @show="dropdownVisible = true"
                  >
                  </ProSpaceDropdown>
                </template>
                <template #body="field">
                  <multi-column
                    v-if="field.data && !getGridLoading"
                    :columns="5"
                    :data="getParamsColumns(field.data)"
                  />
                </template>
              </Column>
              <Column
                field="editedFields"
                :header="$t('adjustmentEditedFields')"
                sortable
                class="adjustments-grid__bordered-column adjustments-grid__column-store"
              >
                <template #body="field">
                  <div class="adjustments-grid__edited-fields-wrapper">
                    <ProSpaceTagsColumn
                      v-if="field.data && !getGridLoading"
                      :tags="getEditedFieldsTags(field.data?.editedFields)"
                      :limit="4"
                      tag-type="rectangle"
                    />
                  </div>
                </template>
              </Column>
              <Column
                field="actions"
                class="adjustments-grid__column-store-amount frozen"
                frozen
                alignFrozen="right"
                style="max-width: 40px; min-width: 40px"
              >
                <template #body="field">
                  <div class="adjustments-grid__column-store-amount-container">
                    <ProSpaceDots
                      v-if="field.data && !getGridLoading"
                      :options="getDots(field.data)"
                      @action="(action) => handleDots(action, field.data, true)"
                    />
                  </div>
                </template>
              </Column>
            </template>
            <template #empty>
              <ProSpaceEmptyGrid
                icon="error-message"
                :title="$t('noDataTitle')"
                :text="$t('noDataTxt')"
              />
            </template>
          </ProSpaceMainGrid>
        </template>
      </ProSpaceGeoLayout>
      <ProSpaceHistoryModal
        ref="history"
        v-model="showHistoryModal"
        modelName="AdjustmentApiModel"
        @close="closeHistory"
      />
    </template>
    <template #right>
      <AdjustmentPanel
        v-if="hasSelect"
        ref="right-panel"
        :periodId="currentPeriod?.id"
        :getDots="getDots"
        :checkAccess="checkAccess"
        :wrapperCheckRoles="wrapperCheckRoles"
        :modelFastFilter="modelFastFilter"
        :selectedStatuses="getSelectedStatuses"
        :adjustmentFilter="adjustmentsFilter"
        :is-all-rows-selected="isAllRowsSelected || isAllSelectedCount"
        :selected-model="selectedModel"
        @clear="selectedMultipleReset"
        @clear:excluded="clearExcluded"
        @open="toItem"
        @refresh="actionUpdate"
        @cancel="showConfirmCancel"
      />
    </template>
  </ProSpaceGeoLayout>
  <ProSpaceFlashMessage
    v-model="showClearModal"
    :applyTxt="$t('confirmTxt')"
    :cancelTxt="$t('cancelTxt')"
    :title="$t('questClearFilters')"
    @apply="actionClear"
    @cancel="actionCancelClear"
    :needHideApply="false"
  >
    {{ $t("bodyClearFilters") }}
  </ProSpaceFlashMessage>
  <ProSpaceFlashMessage
    v-if="showConfirmModel"
    v-model="showConfirmModel"
    :type="confirmType"
    :icon="confirmIcon"
    :title="confirmTitle"
    :applyTxt="confirmApplyTxt"
    :cancelTxt="confirmCancelTxt"
    @apply="applyConfirm"
    @cancel="cancelConfirm"
  >
    {{ confirmText }}
  </ProSpaceFlashMessage>
  <ModalBlockFilters />
</template>
<script>
import {
  ProSpaceFilterPanel,
  ProSpaceCustomFilterPanel,
  ProSpaceMultiSelect,
  ProSpaceMainGrid,
  ProSpaceDots,
  ProSpaceModal,
  ProSpaceGeoLayout,
  ProSpaceTopPanel,
  ProSpaceIconButton,
  ProSpaceButton,
  ProSpaceTableHeaderCell,
  ProSpaceDateColumn,
  ProSpaceDefaultTextColumn,
  ProSpaceEmptyGrid,
  ProSpaceLeftPanel,
  ProSpaceTree,
  ProSpaceVLayout,
  ProSpaceFilterContainsChips,
  ProSpaceFilterDatesRange,
  ProSpaceFilterSearchfield,
  ProSpaceDropdown,
  ProSpaceInputCalendar,
  ProSpaceInputNumber,
  ProSpaceInputSwitch,
  ProSpaceInputText,
  ProSpaceFlashMessage,
  ProSpaceTagsColumn,
} from "@prospace/prospace-components-library";
import Column from "primevue/column";
import AdjustmentPanel from "./components/AdjustmentPanel";
import ProSpaceHistoryModal from "../../../../../Frontends/ProSpace/components/modals/ProSpaceHistoryModal.vue";
import { AdjustmentService } from "../../services/AdjustmentService.js";
import { AgencyService } from "../../services/AgencyService.js";
import { NetworkService } from "../../services/NetworkService";
import { KADLService } from "../../services/KADLService";
import { KASService } from "../../services/KASService";
import { HSRService } from "../../services/HSRService";
import { DashboardService } from "../../services/DashboardService.js";
import { mapGetters } from "vuex";
import accessMixin from "../../../../../Frontends/Common/utils/elementAccess/accessMixin";
const _ = require("lodash");
import moment from "moment";
import { getAMorPM } from "@prospace/prospace-components-library/src/lib-assets/mixins/date";
import StoreColumn from "../AddressProgramOrders/columns/storeColumn.vue";
import MultiColumn from "../AddressProgramOrders/columns/multiColumn.vue";
import sortingOptions from "./sortingOptions";
import { useMultiSelectGrid } from "@composes/multiselect";
import { ref } from "vue";
import ModalBlockFilters from "@multiselect/ModalBlockFilters";
export default {
  mixins: [accessMixin],
  components: {
    MultiColumn,
    StoreColumn,
    ProSpaceFilterPanel,
    ProSpaceCustomFilterPanel,
    ProSpaceMultiSelect,
    ProSpaceMainGrid,
    ProSpaceDots,
    ProSpaceModal,
    Column,
    ProSpaceGeoLayout,
    ProSpaceTopPanel,
    ProSpaceIconButton,
    ProSpaceButton,
    ProSpaceTableHeaderCell,
    ProSpaceDateColumn,
    ProSpaceDefaultTextColumn,
    ProSpaceEmptyGrid,
    AdjustmentPanel,
    ProSpaceHistoryModal,
    ProSpaceLeftPanel,
    ProSpaceTree,
    ProSpaceVLayout,
    ProSpaceFilterContainsChips,
    ProSpaceFilterDatesRange,
    ProSpaceFilterSearchfield,
    ProSpaceDropdown,
    ProSpaceInputCalendar,
    ProSpaceInputNumber,
    ProSpaceInputSwitch,
    ProSpaceInputText,
    ProSpaceFlashMessage,
    ProSpaceTagsColumn,
    ModalBlockFilters,
  },
  setup() {
    const grid = ref(null);
    const filterPanel = ref(null);
    const {
      selectedMultipleGridHandler,
      selectedMultipleClear,
      selectedData,
      selectedCount,
      selectedClude,
      hasSelect,
      selectedMultipleBlockFilterHandler,
      needBlock,
      getRequestModel,
      selectedMultipleReset,
    } = useMultiSelectGrid(grid, filterPanel);
    return {
      grid,
      filterPanel,
      selectedMultipleGridHandler,
      selectedData,
      selectedClude,
      hasSelect,
      selectedMultipleClear,
      selectedMultipleBlockFilterHandler,
      needBlock,
      selectedCount,
      getRequestModel,
      selectedMultipleReset,
    };
  },
  data() {
    return {
      showHistoryModal: false,
      skipCheckChanges: false,
      needSelect: true,
      // Grid Panel
      showClearModal: false,

      // Service
      adjustmentService: new AdjustmentService(),
      agencyService: new AgencyService(),
      networkService: new NetworkService(),
      kadlService: new KADLService(),
      kasService: new KASService(),
      hsrService: new HSRService(),
      dashboardService: new DashboardService(),
      dropdownVisible: false,
      selectCurrentSorting: "id",
      sortingOptions: sortingOptions(this.$t),
      currentSortingLabel: this.$t("adjustmentOutletCode"),
      adjustmentsFilter: "",
      isAllRowsSelected: false,

      //InlinePanel
      masterId: null,

      // Multi filter
      modelFastFilter: {
        label: this.$t("allTxt"),
        value: [
          { field: "disabled", value: false },
          { field: "MarsPeriodId", value: this.currentPeriod?.id },
        ],
      },

      // Confirm Model
      showConfirmModel: false,
      confirmIcon: "",
      confirmType: "",
      confirmTitle: "",
      confirmText: "",
      confirmApplyTxt: "",
      confirmCancelTxt: "",
      applyConfirm: () => {},
      cancelConfirm: null,
      dotsHeaderOptions: [
        { name: "export_xlsx", text: this.$t("export_xlsx"), icon: "download" },
      ],
      isLoadedPeriod: false,
      gridLoaded: false,
    };
  },
  watch: {
    currentPeriod: {
      handler(val, oldVal) {
        if (Object.keys(val).length > 0) {
          if (val?.id !== oldVal?.id) {
            this.setPeriod(val);
          }
        }
      },
      immediate: true,
    },
    selectCurrentSorting(val) {
      const option = this.sortingOptions.find((s) => s.value === val);
      if (option) {
        this.currentSortingLabel = option.label;
      }
      this.dropdownVisible = false;
      this.$nextTick(() => {
        this.grid.sortField = val;
        this.grid.getRecords(true);
        this.adjustmentsFilter = this.grid.getRecords(true, true);
      });
    },
    'grid.loading': {
      handler: function(val) {
        if (!val && !this.gridLoaded) {
          this.loadingHandler(true);
        }
      }
    }
  },
  mounted() {
    const params = this.$route.params;
    if (
      this.filterPanel &&
      this.filterPanel.filter &&
      this.filterPanel.filter.MarsPeriodId
    )
      this.filterPanel.filter.MarsPeriodId = null;
    this.handlerRouteParams();
    window.addEventListener("beforeunload", this.beforeUnload);
    this.loadingHandler(true);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.beforeUnload);
  },
  beforeRouteLeave(to, from) {
    if (this.checkAdjustmentChanges()) {
      this.skipCheckChanges = true;
      this.showConfirmUnsavedChanges(
        () => {
          this.$router.push({ name: to.name });
        },
        () => {
          this.$parent.$parent.$parent.setActiveMenuitem(from);
          this.skipCheckChanges = false;
        }
      );
      return false;
    }

    return true;
  },
  computed: {
    ...mapGetters({
      userinfo: "userinfoStore/getUserinfo",
      getFormatDateTime: "userinfoStore/getFormatDateTime",
      currentPeriod: "marsPeriodStore/getMarsPeriod",
      access: "securityRoleAccess/access",
    }),
    selectedModel() {
      return this.grid.getSelectedModel();
    },
    customServices() {
      return [
        {
          service: new AdjustmentService(),
          getMethod: "getEditedFields",
          optionValue: "value",
          optionLabel: "value",
          type: "checkboxes",
          model: "editedFields",
          label: this.$t("adjustmentEditedFields"),
          selected: {},
          needSelectAll: true,
          crossFilter: true,
        },
        {
          service: new AdjustmentService(),
          type: "checkboxes",
          model: "includeInCoverage",
          getMethod: "getHasAdjustmentFilterCoverage",
          optionValue: "value",
          optionLabel: "text",
          label: this.$t("coverage"),
          placeholder: this.$t("includedInCoverage"),
          needSelectAll: true,
          selected: {},
        },
        {
          type: "chips",
          model: "area",
          label: this.$t("area"),
          selected: {},
          placeholder: this.$t("area"),
        },
        {
          type: "chips",
          model: "address",
          label: this.$t("adjustmentOutletAddressContains"),
          selected: {},
          placeholder: this.$t("adjustmentOutletAddressContains"),
        },
        {
          type: "chips",
          model: "city",
          label: this.$t("city"),
          selected: {},
          placeholder: this.$t("city"),
        },
        {
          type: "chips",
          model: "id",
          label: this.$t("adjustmentId"),
          onlyNumbers: true,
          selected: {},
          placeholder: this.$t("adjustmentId"),
        },
        {
          type: "chips",
          model: "externalCode",
          label: this.$t("externalCode"),
          selected: {},
          placeholder: this.$t("externalCode"),
        },
        {
          type: "date",
          model: "createdAt",
          label: this.$t("date"),
          optionValue: "date",
          selected: {},
          dateFormat: this.userinfo.dateFormat,
        },
      ];
    },
    dotsOptions() {
      let res = [
        {
          name: "open",
          text: this.$t("open"),
          icon: "file-lines",
          method: this.toItem,
          liteRef: "open-option",
          iconSize: "20px",
          iconGap: 7,
          itemClassName: "prospace-dots-item--h34",
        },
        {
          name: "cancel",
          text: this.$t("delete"),
          icon: "ban",
          method: this.showConfirmCancel,
          liteRef: "cancel-option",
          iconSize: "20px",
          iconGap: 5,
          itemClassName: "prospace-dots-item--h34",
        },
      ];
      return res;
    },
    fastFilters() {
      return [
        {
          label: this.$t("allTxt"),
          value: [
            { field: "disabled", value: false },
            { field: "MarsPeriodId", value: this.currentPeriod?.id },
          ],
        },
        {
          label: this.$t("adjustmentOnAcceptance"),
          value: [
            { field: "disabled", value: false },
            { field: "status", value: "onacceptance" },
            { field: "MarsPeriodId", value: this.currentPeriod?.id },
          ],
        },
        {
          label: this.$t("adjustmentOnApproval"),
          value: [
            { field: "disabled", value: false },
            { field: "status", value: "onapproval" },
            { field: "MarsPeriodId", value: this.currentPeriod?.id },
          ],
        },
        {
          label: this.$t("adjustmentComplete"),
          value: [
            { field: "disabled", value: false },
            { field: "status", value: "completed" },
            { field: "MarsPeriodId", value: this.currentPeriod?.id },
          ],
        },
        {
          label: this.$t("adjustmentDeleted"),
          value: [
            { field: "disabled", value: true },
            { field: "status", value: "deleted" },
            { field: "MarsPeriodId", value: this.currentPeriod?.id },
          ],
        },
      ];
    },
    gridData() {
      return this.adjustmentService.transformer.gridData;
    },
    getSelectedStatuses() {
      return this.selectedClude?.items.map((s) => s.status) || [];
    },
    getPageTitle() {
      if (this.userinfo.agencyLinkType === "AGENCY") {
        return `${this.userinfo?.agencyLinkAgencyValue} | ${this.$t(
          "adjustmentsAddressProgram"
        )}`;
      } else {
        return `${this.$t("mars")} | ${this.$t("adjustmentsAddressProgram")}`;
      }
    },
    getSortingFieldValue() {
      const sortingOptions = this.getSortingOptions;
      const option = sortingOptions?.find((o) => o.value === "outletCode");
      return this.selectCurrentSorting === "id"
        ? option?.value
        : this.selectCurrentSorting;
    },
    getDefaultSortingField() {
      return this.selectCurrentSorting === "id"
        ? "id"
        : this.getSortingFieldValue;
    },
    getSortingOptions() {
      return this.sortingOptions;
    },
    isAllSelectedCount() {
      return isNaN(this.selectedCount);
    },
    searchFieldsConfig() {
      const searchFields = [
        {
          model: "agencyId",
          service: this.agencyService,
          optionValue: "id",
          txtTitleModal: this.$t("agencyList"),
          placeholderValue: this.$t("agency"),
        },
        {
          model: "networkValue",
          service: this.networkService,
          optionValue: "value",
          txtTitleModal: this.$t("networkList"),
          placeholderValue: this.$t("network"),
        },
        {
          model: "KADLValue",
          service: this.kadlService,
          optionValue: "value",
          txtTitleModal: this.$t("kadlList"),
          placeholderValue: this.$t("kadl"),
        },
        {
          model: "kasValue",
          service: this.kasService,
          optionValue: "value",
          txtTitleModal: this.$t("kasList"),
          placeholderValue: this.$t("kAS"),
        },
      ];
      if (!this.checkAccess("agencyId-ProSpaceFilterSearchfield")) {
        return searchFields.filter((f) => f.model !== "agencyId");
      } else {
        return searchFields;
      }
    },
    getLoaderConfig() {
      return {
        cols: [30, 50, 20],
        rows: [
          { width: 100, height: 15, borderRadius: "20px" },
          { width: 100, height: 15, borderRadius: "20px" },
        ],
      };
    },
    getSingleLoaderConfig() {
      return {
        cols: [100],
        rows: [
          { width: 100, height: 45, borderRadius: "5px" },
          { width: 100, height: 45, borderRadius: "5px" },
        ],
      };
    },
    getGridLoading() {
      return this.grid.loading;
    },
  },
  methods: {
    clearExcluded() {
      if (this.grid) {
        this.selectedModel = null;
        this.selectedCount = this.grid?.records.length;
        this.selectedClude = {
          items: [],
          isAll: true,
        };
        this.grid.selectedModel = [];
        this.grid.isAllRowsSelected = true;
      }
    },
    checkIsAllRowsSelected() {
      this.$nextTick(() => {
        this.isAllRowsSelected = this.grid.isAllRowsSelected;
      });
    },
    getParamsColumns(data) {
      const {
        createdAt,
        agencyValue,
        area,
        city,
        hsrValue,
        externalCode,
        kadlValue,
        kasValue,
        outletCode,
      } = data;

      let params = [];
      if (this.checkAccess("agency-rows")) {
        params = [
          [
            { title: this.$t("date"), value: this.formateDate(createdAt) },
            { title: this.$t("agency"), value: agencyValue },
            { title: this.$t("area"), value: area },
            { title: this.$t("city"), value: city },
            { title: this.$t("hsr"), value: hsrValue },
          ],
          [
            { title: this.$t("adjustmentOutletCode"), value: outletCode },
            { title: this.$t("externalCode"), value: externalCode },
            { title: this.$t("kadl"), value: kadlValue },
            { title: this.$t("kAS"), value: kasValue },
          ],
        ];
      } else {
        params = [
          [
            { title: this.$t("date"), value: this.formateDate(createdAt) },
            { title: this.$t("area"), value: area },
            { title: this.$t("city"), value: city },
            { title: this.$t("adjustmentOutletCode"), value: outletCode },
          ],
          [
            { title: this.$t("externalCode"), value: externalCode },
            { title: this.$t("kadl"), value: kadlValue },
            { title: this.$t("kAS"), value: kasValue },
          ],
        ];
      }

      return params;
    },
    handlerBlockFilterPanel({ tab }) {
      this.showClearModal = true;
      this.actionClear = () => {
        this.selectedMultipleClear();
        if (tab) {
          this.filterPanel.fastFilter = tab;
        }
        this.$nextTick(() => {
          this.filterPanel.submitFilter(true);
          this.showClearModal = false;
        });
      };
      this.actionCancelClear = () => {
        this.showClearModal = false;
      };
    },
    beforeUnload(event) {
      if (this.checkAdjustmentChanges()) {
        event.returnValue = "";
        event.preventDefault();
      }
    },
    handlerRouteParams() {
      const params = this.$route.params;
      if (params.status) {
        let fastFilter = this.fastFilters.find((x) =>
          x.value.find((f) => f.field == "status" && f.value == params.status)
        );
        if (fastFilter) {
          this.modelFastFilter = fastFilter;
          this.filterPanel.fastFilter = this.modelFastFilter;
          this.filterPanel.submitFilter(false, false);
        }
      } else {
        this.filterPanel?.submitFilter();
      }
    },
    closeHistory() {
      this.showHistoryModal = false;
    },
    showHistory(record) {
      this.$refs.history.tabs[0].masterId = record.id;
      this.$refs.history.masterName = record.code;
      this.$nextTick(() => {
        this.showHistoryModal = true;
      });
    },
    getDots(data) {
      if (data.status === "completed" || data.status === "deleted")
        return this.dotsOptions.filter((x) => x.name != "cancel");

      return this.wrapperCheckRoles(this.dotsOptions);
    },
    setPeriod(period) {
      this.skipCheckChanges = true;
      this.selectedMultipleClear();
      this.filterPanel?.submitFilter(true);
      this.$nextTick(() => {
        this.skipCheckChanges = false;
      });
    },
    toItem(data) {
      if (this.checkAdjustmentChanges()) {
        this.showConfirmUnsavedChanges(() => {
          this.toItemHandler(data);
        });
      } else {
        this.toItemHandler(data);
      }
    },
    toItemHandler(data) {
      const { agencyValue } = data;
      this.$router.push({
        path: `/addressprogram/adjustment/${data.id}?period=${this.currentPeriod?.id}&agency=${agencyValue}`,
      });
    },
    submitFilter({ filter, search }) {
      filter["MarsPeriodId"] = {
        and: [this.currentPeriod.id],
      };
      this.setModelFastFilter(filter);
      this.grid.submitFilter(filter, search);
      this.adjustmentsFilter = this.grid.getRecords(true, true);
      this.gridLoaded = false;
      this.loadingHandler(true);
    },
    emitFilter(filterResult = false) {
      this.filterPanel.submitFilter();
      if (filterResult) {
        this.$refs.filterResult.close();
      }
    },
    customFilterApply(filter, model) {
      filter = Object.assign(filter, model);
      this.gridLoaded = false;
      this.loadingHandler(true);
      this.filterPanel.submitFilter();
    },
    actionPeriod() {
      if (this.checkAdjustmentChanges()) {
        this.showConfirmUnsavedChanges(() => {
          this.actionPeriodHandler();
        });
      } else {
        this.actionPeriodHandler();
      }
    },
    actionPeriodHandler() {
      this.$store.commit("marsPeriodStore/SET_PERIODMODALSTATE", true);
    },
    actionUpdate() {
      if (this.checkAdjustmentChanges()) {
        this.showConfirmUnsavedChanges(() => {
          this.update();
        });
      } else {
        this.update();
      }
    },
    updateRightPanel() {
      if (this.$refs["right-panel"]) {
        this.$refs["right-panel"].update();
      }
    },
    update() {
      this.grid.getRecords(true);
      this.updateRightPanel();
      this.$nextTick(() => {
        this.loadingHandler(true);
      });
      this.grid.resetSkip();
    },
    loadingHandler(val) {
      this.grid ? (this.grid.loading = val) : "";
    },
    selectedHandler(val) {
      this.checkIsAllRowsSelected();
      if (this.checkAdjustmentChanges()) {
        this.showConfirmUnsavedChanges(
          () => {
            this.selectedMultipleGridHandler(val);
          },
          () => {
            this.selectedMultipleGridHandler(val);
            this.skipCheckChanges = true;
            this.needSelect = false;
          }
        );
      } else {
        if (this.needSelect) this.selectedMultipleGridHandler(val);
        this.skipCheckChanges = false;
        this.needSelect = true;
      }
    },
    showConfirmUnsavedChanges(action, cancelAction = () => {}) {
      this.confirmType = "default";
      this.confirmIcon = "circle-question";
      this.confirmTitle = this.$t("exitFromPageConfirmTxt");
      this.confirmText =
        this.$t("unsavedChangesTxt") + "\n" + this.$t("exitFromPageTxt");
      this.confirmApplyTxt = this.$t("confirmTxt");
      this.confirmCancelTxt = this.$t("cancelTxt");
      this.applyConfirm = action;
      this.cancelConfirm = cancelAction;
      this.showConfirmModel = true;
    },
    showConfirmCancel(models, fromRow = false) {
      this.confirmType = "default";
      this.confirmIcon = "flash-success";
      this.confirmTitle = this.$t("adjustmentDeleteAll");
      if (typeof models === "object") {
        this.confirmText = this.$t("adjustmentCancelOneQuestion");
      } else {
        this.confirmText =
          Array.isArray(models) && models.length > 1
            ? this.$t("adjustmentCancelQuestion")
            : this.$t("adjustmentCancelAllQuestion");
      }
      this.confirmApplyTxt = this.$t("confirmTxt");
      this.confirmCancelTxt = this.$t("cancelTxt");
      if (fromRow) {
        this.applyConfirm = () => {
          this.cancel(Array.isArray(models) ? models : [models], true);
        };
      } else {
        this.applyConfirm = () => {
          this.cancel(Array.isArray(models) ? models : [models]);
        };
      }
      this.showConfirmModel = true;
    },
    cancel(models, fromRow = false) {
      const requestModel = this.getRequestModel(models);
      const requestConfig = {
        data: !fromRow ? requestModel.records : models,
        filter: requestModel.filter,
      };
      this.adjustmentService.delete(requestConfig).then(() => {
        this.actionUpdate();
        this.selectedMultipleClear();
      });
    },
    formateDate(date) {
      return (
        this.getFormatDateTime(date) +
        " " +
        getAMorPM(date, this.userinfo?.timeFormat)
      );
    },
    getStatus(status) {
      if (!status) return;

      let type = "default";
      switch (status) {
        case "onacceptance":
          type = "light-blue";
          break;
        case "onapproval":
          type = "blue";
          break;
        case "completed":
          type = "teal";
          break;
        case "deleted":
          type = "draft";
          break;
      }

      return {
        type: type,
        text:
          status !== "deleted" ? this.$t(status) : this.$t("adjustmentDeleted"),
      };
    },
    handleDots(action, item, fromRow) {
      var option = this.dotsOptions.find((o) => o.name === action);
      option.method(item, fromRow);
    },
    checkAdjustmentChanges() {
      if (this.skipCheckChanges) return false;

      let panel = this.$refs["right-panel"];
      return panel ? panel.isUnsavedChanges : false;
    },
    setModelFastFilter(filter) {
      if (filter) {
        const { status } = filter;
        if (status && status["and"].length) {
          const filterStatus = status["and"][0];
          const me = this;
          this.fastFilters.forEach((filter, index) => {
            const model = filter?.value.find(
              (el) => el.field === "status" && el.value === filterStatus
            );
            if (model) {
              this.$nextTick(() => {
                me.modelFastFilter = me.fastFilters[index];
              });
            }
          });
        } else {
          this.$nextTick(() => {
            this.modelFastFilter = this.fastFilters[0];
          });
        }
      } else {
        if (this.modelFastFilter.value.length === 0) {
          this.$nextTick(() => {
            this.modelFastFilter = this.fastFilters[0];
          });
        }
      }
    },
    updateModelFastFilter(evt) {
      if (evt) {
        this.modelFastFilter = evt;
      }
      this.$nextTick(() => {
        this.loadingHandler(true);
        this.selectedMultipleClear();
      });
    },
    handleHeaderDots(action) {
      switch (action) {
        case "export_xlsx":
          this.actionExport("XLSX");
          break;
      }
    },
    actionExport(format) {
      let me = this;
      me.loadingHandler(true);
      me.adjustmentService
        .export(
          me.currentPeriod.name,
          me.$refs.grid.getRecords(false, true),
          format
        )
        .then(() => me.loadingHandler(false));
      //TODO: Catch error?
      //TODO: Show message(export task created)
    },
    gridLoadedHandler() {
      setTimeout(() => {
        this.loadingHandler(false);
        this.gridLoaded = true;
      }, 1000);
    },
    getEditedFieldsTags(tags) {
      return tags.split(', ').filter(tag => {
        // return !String(tag).match(/[^a-zA-Za-åa-ö-w-я0-9"#¨'_]*/)[0];
        return !tag.includes('%');
      });
    },
  },
};
</script>
<style lang="scss">
.adjustments-grid {
  .prospace-left-panel {
    border-bottom: none;
  }
  th.adjustments-grid__sorting-table-header {
    .p-column-header-content {
      width: 100% !important;
    }
  }
  th.adjustments-grid__column-store {
    .p-column-resizer {
      right: 0 !important;
    }
  }
  td.adjustments-grid__column-store {
    padding: 10px 0 !important;
    .adjustments-grid__column-store-container {
      height: 100%;
      border-right: 1px dashed var(--prospace-ui-border-color);
    }
  }

  th.adjustments-grid__bordered-column {
    .p-column-title {
      padding-left: 10px !important;
    }
    .p-column-resizer {
      right: 0 !important;
    }
  }
  th:nth-child(2) {
    .p-column-title {
      padding-left: 0 !important;
    }
  }
  td.adjustments-grid__bordered-column {
    padding: 10px 0 !important;
    .p-multi-column {
      border-right: 1px dashed var(--prospace-ui-border-color);
    }
    &.no-right {
      .p-multi-column {
        border-right: none;
      }
    }
  }

  th.adjustments-grid__column-store-amount {
    .p-column-title {
      padding-left: 10px !important;
    }
  }
  td.adjustments-grid__column-store-amount {
    padding: 10px 0 !important;
    &.frozen {
      .adjustments-grid__column-store-amount-container {
        display: flex !important;
        justify-content: center;
        align-items: center;
        height: 100%;
        border-left: 1px dashed var(--prospace-ui-border-color) !important;
      }
    }
  }
  .column-start {
    align-items: flex-start !important;
  }

  .adjustments-grid__dropdown .p-dropdown {
    background: none;
    border: none;
    height: auto;
  }
  .adjustments-grid__dropdown .prospace-dropdown-panel {
    width: 178px;
    position: absolute !important;
    right: 0 !important;
    left: auto !important;
    top: 24px !important;
    border: var(--prospace-ui-border);
  }
  .adjustments-grid__dropdown
    .p-dropdown-panel
    .p-dropdown-items
    .p-dropdown-item {
    padding: 7.5px 10px;
  }
  .adjustments-grid__dropdown
    .p-dropdown-panel
    .p-dropdown-items
    .p-dropdown-item:not(.p-highlight):not(.p-disabled).p-focus,
  .adjustments-grid__dropdown
    .p-dropdown-panel
    .p-dropdown-items
    .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover {
    background-color: var(--prospace-ui-row-select);
  }
  &__dropdown {
    position: absolute;
    width: auto !important;
    order: 3;
    top: -2px;
    right: 0;
    max-width: 20px;
    max-height: 20px;
    .p-dropdown-trigger {
      width: auto;
      margin-left: auto;
    }
  }
}
</style>
